<template>
    <div class="w-full">
        <div class="flex items-center xl:hidden">
            <button
                id="open-mobile-navigation"
                @click.stop="emit('openMobileNavigation')"
                :aria-label="$snippets['headless.navigation.openMenu']">
                <Bars3Icon class="size-5 stroke-2 lg:size-6" />
            </button>
            <UiUserAccountButton />
        </div>
        <div class="hidden xl:block">
            <nav>
                <ul class="flex">
                    <li v-for="navigationElement in navigationElements">
                        <NuxtLink
                            v-if="navigationElement.type === 'page' && navigationElement.seoUrls"
                            :to="'/' + navigationElement?.seoUrls[0]?.seoPathInfo"
                            :class="{
                                activeLadiesContext: ladiesContext,
                                activeMenContext: menContext,
                                activeLadiesPage: isActiveLadiesPage(navigationElement),
                                activeMenPage: isActiveMenPage(navigationElement),
                            }"
                            class="navigation-item">
                            {{ navigationElement?.translated?.name }}
                        </NuxtLink>
                        <NuxtLink
                            v-else-if="navigationElement.type === 'link' && getUrlById(navigationElement.internalLink)"
                            :to="getUrlById(navigationElement.internalLink) || undefined"
                            :class="{
                                activeLadiesContext: ladiesContext,
                                activeMenContext: menContext,
                                activeLadiesPage: isActiveLadiesPage(navigationElement),
                                activeMenPage: isActiveMenPage(navigationElement),
                            }"
                            class="navigation-item">
                            {{ navigationElement?.translated?.name }}
                        </NuxtLink>
                    </li>
                </ul>
            </nav>
        </div>
    </div>

    <transition name="slide" mode="out-in">
        <div
            id="mobile-navigation"
            class="fixed top-0 bottom-0 left-0 z-[70] flex h-[100dvh] w-full flex-col bg-white lg:w-96"
            v-if="isOpen">
            <button class="absolute top-4 right-4 z-40 hidden size-[50px] bg-green-600" @click="closeMobileNavigation">
                <XMarkIcon class="mx-auto size-6 stroke-2" />
            </button>
            <div class="flex h-full flex-col overflow-x-hidden overflow-y-scroll">
                <div class="xl:hidden">
                    <UiHeaderUSPs />
                    <div class="flex justify-between">
                        <div @click="emit('showSearchPopup')" class="flex-1">
                            <!-- Mobile menu search input -->
                            <div class="flex items-center px-7 py-[13px] xl:hidden">
                                <MagnifyingGlassIcon class="mr-5 size-6 stroke-2 !text-black" />
                                <input
                                    v-model="model"
                                    id="search-input-mobile-menu"
                                    class="w-full text-xs leading-none tracking-[0.6px] placeholder:!text-black focus:!outline-none focus-visible:outline-0"
                                    type="text"
                                    role="searchbox"
                                    :aria-description="$snippets['headless.header.lookingFor']"
                                    :placeholder="$snippets['headless.header.lookingFor']"
                                    @keyup.enter="handleEnter" />
                            </div>
                        </div>
                        <button
                            :aria-label="$snippets['headless.navigation.closeMenu']"
                            class="z-10 size-[50px] bg-green-600"
                            @click="closeMobileNavigation">
                            <XMarkIcon class="mx-auto size-6 stroke-2 !text-black" />
                        </button>
                    </div>
                    <div class="border-y border-green-500">
                        <nav class="flex h-full items-center">
                            <ul class="flex w-full gap-6 overflow-x-auto pr-7">
                                <li
                                    v-for="navigationElement in navigationElements"
                                    class="shrink-0 pt-[25px] pb-[21px] first:ml-7">
                                    <button
                                        @click="emit('showChildren', navigationElement)"
                                        v-if="navigationElement.visibleChildCount > 0"
                                        :class="{
                                            '!text-black':
                                                selectedNavigationElement === navigationElement?.translated?.name,
                                        }"
                                        class="navigation-item !mr-0">
                                        {{ navigationElement?.translated?.name }}
                                    </button>
                                    <NuxtLink
                                        :to="'/' + navigationElement?.seoUrls[0]?.seoPathInfo"
                                        @click="closeMobileNavigation"
                                        v-else
                                        :class="{
                                            '!text-black':
                                                selectedNavigationElement === navigationElement?.translated?.name,
                                        }"
                                        class="navigation-item !mr-0">
                                        {{ navigationElement?.translated?.name }}
                                    </NuxtLink>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
                <div class="normal-container grow">
                    <nav>
                        <ul>
                            <li>
                                <NuxtLink
                                    v-for="navigationElementsChild in navigationElementsChildren"
                                    class="font-monument-extended flex w-full items-center justify-between border-b !border-green-500 px-2 py-4 text-sm !text-black transition-all last:border-none"
                                    :to="getPageUrl(navigationElementsChild)"
                                    @click="closeMobileNavigation">
                                    <span :class="{ 'sale-tag': navigationElementsChild?.translated?.name === 'SALE' }">
                                        {{ navigationElementsChild?.translated?.name }}
                                    </span>
                                    <ChevronRightIcon class="size-6 stroke-2" />
                                </NuxtLink>
                            </li>
                        </ul>
                    </nav>
                </div>
                <div class="bg-green-300 py-10">
                    <nav class="normal-container">
                        <ul>
                            <li>
                                <NuxtLink
                                    class="mobile-bottom-link"
                                    @click="closeMobileNavigation"
                                    :to="$snippets['headless.header.logInUrl']">
                                    <UserCircleIcon class="mr-2 size-6 stroke-2" />
                                    {{
                                        userCookies
                                            ? `Hallo ${userCookies.firstName}`
                                            : $snippets['headless.header.logInTitle']
                                    }}
                                </NuxtLink>
                            </li>
                            <li>
                                <NuxtLink
                                    class="mobile-bottom-link"
                                    @click="closeMobileNavigation"
                                    :to="$snippets['headless.header.contactAndOpeningHoursUrl']">
                                    {{ $snippets['headless.header.contactAndOpeningHoursTitle'] }}
                                </NuxtLink>
                            </li>
                            <li>
                                <NuxtLink
                                    class="mobile-bottom-link"
                                    @click="closeMobileNavigation"
                                    :to="$snippets['headless.header.faqUrl']">
                                    {{ $snippets['headless.header.faqTitle'] }}
                                </NuxtLink>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>
    </transition>
</template>

<script setup lang="ts">
    import {
        Bars3Icon,
        ChevronRightIcon,
        MagnifyingGlassIcon,
        UserCircleIcon,
        XMarkIcon,
    } from '@heroicons/vue/24/outline';
    const { getUrlById } = useCategorySeoUrl();
    const { userCookies } = useUserExtended();
    const route = useRoute();
    const model = defineModel('searchQuery');

    const emit = defineEmits([
        'openMobileNavigation',
        'closeMobileNavigation',
        'showChildren',
        'showSearchPopup',
        'closeSearchPopup',
    ]);

    const props = defineProps<{
        navigationElements: Array<any> | null;
        selectedNavigationElement: any;
        navigationElementsChildren: Array<any> | null;
        isOpen: boolean;
        data: any;
    }>();

    const headerBarShopTheme = computed<'men' | 'ladies' | null>(() => {
        const path = route.path;
        if (path.includes('/heren')) {
            return 'men';
        }
        if (path.includes('/dames')) {
            return 'ladies';
        }
        if (/^\/[a-z]{2}\/?$/i.test(path)) {
            return null;
        }
        const ctx = props.data?.context;
        if (ctx === 'men') {
            return 'men';
        }
        if (ctx === 'ladies') {
            return 'ladies';
        }
        return null;
    });

    const ladiesContext = computed(() => headerBarShopTheme.value === 'ladies');
    const menContext = computed(() => headerBarShopTheme.value === 'men');

    function navigateToPage(url: string) {
        navigateTo(url);
        closeMobileNavigation();
    }

    function closeMobileNavigation() {
        emit('closeMobileNavigation');
    }

    function isActiveLadiesPage(navigationItem: any) {
        return ladiesContext.value && navigationItem.translated.name === 'Dames';
    }

    function isActiveMenPage(navigationItem: any) {
        return menContext.value && navigationItem.translated.name === 'Heren';
    }

    function handleEnter() {
        if (model.value.trim()) {
            navigateTo(`/search?tn_q=${encodeURIComponent(model.value)}`);
            emit('closeSearchPopup');
            closeMobileNavigation();
        }
    }

    function getPageUrl(navigationElementsChild: any): string {
        return navigationElementsChild.seoUrls
            ? '/' + navigationElementsChild?.seoUrls[0]?.seoPathInfo
            : navigationElementsChild.type === 'link' && navigationElementsChild.internalLink
              ? getUrlById(navigationElementsChild.internalLink) || navigationElementsChild.externalLink
              : navigationElementsChild.externalLink;
    }
</script>
